import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, FileText, CheckCircle2, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const RealisationDetails = () => {
  const { id } = useParams();
  const [projet, setProjet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchProjet = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/realisations/${id}`);
        if (isMounted) setProjet(res.data);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          if (isMounted) setError(true);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchProjet();
    return () => { isMounted = false; controller.abort(); };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-900 mb-4" />
        <p className="text-slate-500 font-medium">Chargement du projet en cours...</p>
      </div>
    );
  }

  if (error || !projet) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Projet introuvable</h2>
          <p className="text-slate-500 mb-8 leading-relaxed">
            Le projet que vous recherchez n'existe pas, a été retiré, ou le serveur est temporairement indisponible.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
              Réessayer
            </Button>
            <Link to="/realisations" className="w-full">
              <Button className="w-full bg-[#0A192F] hover:bg-blue-900 text-white">
                Retour au catalogue
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=1200&auto=format&fit=crop";
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${imagePath}`;
    if (imagePath.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${imagePath}`;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${imagePath}`;
  };

  return (
    <div className="bg-slate-50 min-h-screen pb-20 font-sans">
      {/* Hero Image Section */}
      <div className="relative h-[40vh] md:h-[50vh] bg-[#0A192F]">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/60 to-transparent z-10"></div>
          <img 
            src={getImageUrl(projet.image)} 
            onError={(e) => { 
              console.log("Image load error for details", projet.titre, projet.image);
              e.target.src = "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=1200&auto=format&fit=crop"; 
            }}
            alt={projet.titre} 
            className="w-full h-full object-cover opacity-90"
          />
        </div>
        
        <div className="absolute top-6 left-6 z-20">
          <Link to="/realisations" className="bg-white/10 backdrop-blur-md hover:bg-white/20 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-colors border border-white/20">
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-4 relative z-20 -mt-24">
        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 md:p-12">
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="bg-blue-50 text-[#0A192F] border border-blue-200 px-3 py-1.5 rounded-md text-sm font-bold shadow-sm">
              {projet.secteur}
            </span>
            <span className={`px-3 py-1.5 rounded-md text-sm font-bold shadow-sm border ${
              projet.statut === 'Inauguré' || projet.statut === 'Achevé' 
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                : 'bg-slate-50 text-slate-700 border-slate-200'
            }`}>
              {projet.statut}
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0A192F] mb-6 leading-tight tracking-tight">
            {projet.titre}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm font-semibold text-slate-500 mb-10 pb-8 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-900" /> {projet.date_realisation}
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-900" /> {projet.lieu}
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <FileText className="w-5 h-5" /> Réf: {projet.reference}
            </div>
          </div>

          <div className="prose prose-lg prose-slate max-w-none mb-12">
            <p className="text-slate-700 leading-relaxed">
              {projet.description}
            </p>
          </div>
          <div className="bg-[#0A192F] rounded-xl p-8 text-white">
            <h4 className="font-bold text-amber-400 uppercase tracking-wider text-sm mb-2">Gouvernance Provinciale</h4>
            <p className="text-blue-100 leading-relaxed font-medium">
              Ce projet s'inscrit dans la vision de développement impulsée par Son Excellence Fifi Masuka Saini, visant à doter le Lualaba d'infrastructures modernes et de services sociaux de proximité.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealisationDetails;
